import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Bar,
  Line
} from 'recharts';

import SURVEY_CHART_TYPE from '../constants/SurveyChartTypes';
import SurveyChartUtils from '../shared/SurveyChartUtils';
import { chain } from 'lodash';
import SURVEY_VALUE_PRESENTATION_TYPE from '../constants/SurveyValuePresentationTypes';
import AutoCalculatedSurveyMap from '../shared/AutoCalculatedSurveyMap';

const CHART_HEIGHT = 420;
const CHART_Y_AXIS_IDS = {
  ANSWER: 'answer',
  RESPONSE: 'response'
};

/**
 * @typedef {object} SurveyChartProps
 * @property {Array<*>} [surveyResults]
 */

/**
 * @param {SurveyChartProps} props
 */
const SurveyChart = props => {
  const { surveyResults } = props;
  const chartData = mergeSurveyChartData(surveyResults);
  const responseTicks = SurveyChartUtils.generateResponseTicks(surveyResults);
  const answerTicks = SurveyChartUtils.generateAnswerTicks(surveyResults);
  const showSignedValues = isSignedPresentation(surveyResults);

  return (
    <ResponsiveContainer minWidth='60%' width='100%' height={CHART_HEIGHT}>
      <ComposedChart data={chartData} reverseStackOrder>
        <CartesianGrid vertical={false} yAxisId={CHART_Y_AXIS_IDS.ANSWER} />
        <XAxis dataKey='name' />
        <YAxis
          yAxisId={CHART_Y_AXIS_IDS.ANSWER}
          ticks={answerTicks}
          tickCount={11}
          tickFormatter={t => `${t}%`}
        />
        <YAxis
          yAxisId={CHART_Y_AXIS_IDS.RESPONSE}
          orientation='right'
          tickCount={11}
          ticks={responseTicks}
          tickFormatter={showSignedValues ? t => (t > 0 ? `+${t}` : t) : undefined}
        />

        {surveyResults.map(result =>
          result.ChartType === SURVEY_CHART_TYPE.LINE ? (
            <Line
              key={result.Label}
              dataKey={result.Label}
              yAxisId={CHART_Y_AXIS_IDS.RESPONSE}
              stroke={result.Color}
              strokeWidth={5}
              dot={false}
            />
          ) : (
            <Bar
              key={result.Label}
              dataKey={result.Label}
              yAxisId={CHART_Y_AXIS_IDS.ANSWER}
              stackId='total'
              fill={result.Color}
            />
          )
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const mergeSurveyChartData = surveyResults =>
  chain(surveyResults)
    .flatMap(result =>
      result.SurveyResultValues.map(({ Year, Value }) => {
        let numericValue = Value;

        if (result.IsAutoCalculated) {
          const autoCalc = AutoCalculatedSurveyMap[result.QuestionId];
          if (autoCalc?.getValue) {
            numericValue = autoCalc.getValue({ year: Year, allResults: surveyResults });
          }
        }

        return {
          Year,
          name: Year.toString(),
          [result.Label]: numericValue
        };
      })
    )
    .groupBy('Year')
    .map((group, year) => Object.assign({ Year: Number(year), name: year }, ...group))
    .sortBy('Year')
    .value();

const isSignedPresentation = results =>
  results.some(r => r.Presentation === SURVEY_VALUE_PRESENTATION_TYPE.SignedInteger);

export default SurveyChart;
